import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { KeyboardEvent, MouseEvent } from 'react'
import { AlertTriangle } from 'lucide-react'
import { SectionStatusPill } from '@/components/drafting/SectionStatusPill'
import { CitationTooltip, type CitationTooltipState } from '@/components/drafting/CitationTooltip'
import { getAvatarColor, getInitials } from '@/lib/avatar'
import { cn } from '@/lib/utils'
import type { CitationRef, FullResponseSection as Section } from '@/lib/api'
import type { SectionAssignment } from '@/stores/section-assignments-store'
import type { MergedReviewFinding } from '@/lib/schemas'
import { provenanceFor, sanitizeResponseHtml, sectionDomId } from './provenance'
import { clearHighlights, collapse, highlightPassage } from './passage-highlight'
import { decorateToDos } from './annotation-chips'
import { decorateCitations } from '@/components/full-response/citation-markers'

interface FullResponseSectionProps {
  index: number
  section: Section
  assignment: SectionAssignment | undefined
  canEdit: boolean
  evaluatorView: boolean
  onEdit: (sectionKey: string) => void
  /** Open AI-review issues anchored to this section. */
  issues: MergedReviewFinding[]
  /** The one passage to scroll to, set when the reader jumps from the Checks tab. */
  highlightExcerpt: string | null
  onIssueMarkerClick: (sectionKey: string) => void
}

/**
 * One section of the reader: workflow margin (120px) beside a prose column
 * (max 700px — the strip above the reader is sized to margin + gap + prose,
 * 848px, so the two stay aligned).
 * Self-contained by sectionKey so edit-in-place can later swap the prose
 * column for an editor without touching the page.
 */
export function FullResponseSection({
  index,
  section,
  assignment,
  canEdit,
  evaluatorView,
  onEdit,
  issues,
  highlightExcerpt,
  onIssueMarkerClick,
}: FullResponseSectionProps) {
  const proseRef = useRef<HTMLDivElement | null>(null)
  // Preview shows the export's own stripped HTML, so it has no to-dos or
  // citation markers to style; the working view gets the editor's to-do chips
  // and citation badges. To-dos first: a badge splits its text node, and a
  // to-do is only matched within one.
  const html = useMemo(
    () =>
      sanitizeResponseHtml(
        evaluatorView
          ? section.evaluatorHtml
          : decorateCitations(decorateToDos(section.html), section.segments),
      ),
    [evaluatorView, section.evaluatorHtml, section.html, section.segments],
  )

  // Citation tooltip — the editor's, keyed by segment id (the badges carry
  // their segment in `data-section-id`, as the editor's carry theirs).
  const [citationTooltip, setCitationTooltip] = useState<CitationTooltipState | null>(null)
  const closeCitationTooltip = useCallback(() => setCitationTooltip(null), [])
  const citationRefsBySegmentId = useMemo(
    () => new Map<string, CitationRef[]>((section.segments ?? []).map((s) => [s.segmentId, s.citationRefs])),
    [section.segments],
  )
  const libraryCitationRefsBySegmentId = useMemo(
    () => new Map<string, CitationRef[]>((section.segments ?? []).map((s) => [s.segmentId, s.libraryCitationRefs])),
    [section.segments],
  )
  const openCitationFrom = (target: EventTarget | null): boolean => {
    if (!(target instanceof Element)) return false
    const badge = target.closest<HTMLElement>('.citation-badge-inline')
    if (!badge || !proseRef.current?.contains(badge)) return false
    const citationIndex = parseInt(badge.dataset['citationIndex'] ?? '', 10)
    if (!(citationIndex >= 1)) return false
    setCitationTooltip({
      citationIndex,
      sectionId: badge.dataset['sectionId'] ?? '',
      citationType: badge.dataset['citationType'] === 'library' ? 'library' : 'tender',
      anchorRect: badge.getBoundingClientRect(),
    })
    return true
  }
  const handleProseClick = (event: MouseEvent<HTMLDivElement>) => {
    openCitationFrom(event.target)
  }
  const handleProseKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return
    if (openCitationFrom(event.target)) event.preventDefault()
  }
  // Preview has no markers; a tooltip left open would point at nothing.
  useEffect(() => {
    if (evaluatorView) setCitationTooltip(null)
  }, [evaluatorView])
  // Re-applied whenever the prose or the issue set changes: the marks live in
  // DOM that dangerouslySetInnerHTML owns, so React will happily replace them.
  useEffect(() => {
    const el = proseRef.current
    if (!el) return
    clearHighlights(el)
    if (evaluatorView) return
    // Keyed by the collapsed excerpt so two issues quoting the same passage —
    // or a jump target that IS one of the issues — mark it once. Highlighting
    // the same text twice nests a <mark> inside a <mark>.
    const marks = new Map<string, HTMLElement>()
    for (const issue of issues) {
      if (!issue.excerpt) continue
      const key = collapse(issue.excerpt)
      if (key.length === 0 || marks.has(key)) continue
      const mark = highlightPassage(el, issue.excerpt)
      if (mark) marks.set(key, mark)
    }
    if (highlightExcerpt) {
      const key = collapse(highlightExcerpt)
      // Scroll to the jump target's own mark, not whichever mark comes first.
      const target = marks.get(key) ?? highlightPassage(el, highlightExcerpt)
      target?.scrollIntoView({ block: 'center', behavior: 'auto' })
    }
  }, [html, evaluatorView, issues, highlightExcerpt])

  const provenance = provenanceFor(assignment, section.updatedAt)
  const owner = assignment?.userId ? assignment : null
  const color = getAvatarColor(owner?.userId)

  return (
    <section
      id={sectionDomId(section.sectionKey)}
      data-section-key={section.sectionKey}
      className="group mx-auto mb-6 grid w-full max-w-[848px] grid-cols-[120px_minmax(0,1fr)] gap-7"
    >
      <div className="flex flex-col items-end gap-1.5 pt-1 text-right">
        {!evaluatorView && (
          <>
            <div className="flex items-center gap-1.5">
              <SectionStatusPill status={assignment?.collaborationStatus ?? null} />
              {owner && (
                <span
                  className={cn(
                    'inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full text-[8px] font-medium text-white',
                    color.bg,
                  )}
                  title={owner.fullName ?? owner.email ?? undefined}
                >
                  {owner.avatarUrl ? (
                    <img src={owner.avatarUrl} alt="" className="h-full w-full rounded-full object-cover" />
                  ) : (
                    getInitials(owner.fullName)
                  )}
                </span>
              )}
            </div>
            {provenance && (
              <span
                className={cn(
                  'text-meta leading-[1.4]',
                  provenance.stale && 'text-subtle',
                )}
              >
                {provenance.text}
                {provenance.stale && ' · Edited since'}
              </span>
            )}
            {issues.length > 0 && (
              <button
                type="button"
                onClick={() => onIssueMarkerClick(section.sectionKey)}
                aria-label={`${issues.length} ${issues.length === 1 ? 'issue' : 'issues'} in this section`}
                className="inline-flex items-center gap-1 text-meta text-[hsl(var(--status-warning))]"
              >
                <AlertTriangle className="h-3.5 w-3.5" />
                {issues.length}
              </button>
            )}
            {canEdit && (
              <button
                type="button"
                onClick={() => onEdit(section.sectionKey)}
                className="text-meta text-[hsl(var(--brand))] opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100"
              >
                Edit section
              </button>
            )}
          </>
        )}
      </div>

      <div className="min-w-0 max-w-[700px]">
        <h3 className="mb-1.5 flex items-baseline gap-2 text-base font-semibold tracking-[-0.01em] text-foreground">
          <span className="text-sm font-medium tabular-nums text-subtle">{index + 1}</span>
          {canEdit ? (
            <button
              type="button"
              onClick={() => onEdit(section.sectionKey)}
              title="Open this section in the editor"
              className="rounded-sm text-left underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {section.title}
            </button>
          ) : (
            section.title
          )}
        </h3>
        {html ? (
          <div
            ref={proseRef}
            className="prose prose-sm max-w-none text-foreground"
            onClick={handleProseClick}
            onKeyDown={handleProseKeyDown}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        ) : (
          <p className="text-sm italic text-subtle">Nothing drafted yet.</p>
        )}
        <CitationTooltip
          state={citationTooltip}
          onClose={closeCitationTooltip}
          citationRefsBySectionId={citationRefsBySegmentId}
          libraryCitationRefsBySectionId={libraryCitationRefsBySegmentId}
        />
      </div>
    </section>
  )
}
